import type { FC } from "react"
import { useState } from "react"
import { Trans } from "react-i18next"
import type { DashboardUser, Event, Visit, Integration, ExtraCard } from "../models/dashboard"
import IntegrationCard from "./IntegrationCard"
import useUpload from "@app/hooks/useUpload"
import { useChangeAvatar } from "../../agency-users/hooks/useAgencyUsers"
import useEnv from "@app/hooks/useEnv"

interface LeftSidebarProps {
  user: DashboardUser
  eventos: Event[]
  visits: Visit[]
  integrations: Integration[]
  extraCards: ExtraCard[]
}

const LeftSidebar: FC<LeftSidebarProps> = ({ user, eventos, visits, integrations, extraCards }) => {
  const { geconturUrl } = useEnv()
  const agencyCover = user.agency.cover || `${geconturUrl}/img/miraflores.jpg`
  const [uploading, setUploading] = useState(false)
  const [avatarVersion, setAvatarVersion] = useState(0)
  const [avatarError, setAvatarError] = useState(false)
  const changeAvatarMutation = useChangeAvatar()

  const handleAvatarUpload = useUpload(
    async (files: File | FileList) => {
      const file = files as File
      setUploading(true)
      try {
        await changeAvatarMutation.mutateAsync({ userId: user.id, file })
        setAvatarVersion((v) => v + 1)
      } finally {
        setUploading(false)
      }
    },
    { accept: "image/*" },
  )

  return (
    <div className="space-y-4">
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div
          className="relative bg-cover bg-center min-h-[200px] flex flex-col justify-end"
          style={{ backgroundImage: `url(${agencyCover})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
          <div className="relative px-4 pb-4 text-center">
            <div className="relative inline-block">
              {avatarError ? (
                <div className="w-20 h-20 rounded-full border-2 border-white mx-auto flex items-center justify-center bg-blue-100 text-blue-600 text-xl font-bold">
                  {user.fullName?.charAt(0)?.toUpperCase() || "?"}
                </div>
              ) : (
                <img
                  src={(user.avatar || "") + (avatarVersion ? `?v=${avatarVersion}` : "")}
                  alt={user.fullName}
                  className="w-20 h-20 rounded-full object-cover border-2 border-white mx-auto"
                  style={{ filter: uploading ? "brightness(0.7)" : "none" }}
                  onError={() => setAvatarError(true)}
                />
              )}
              <button
                type="button"
                onClick={handleAvatarUpload}
                disabled={uploading || changeAvatarMutation.isPending}
                style={{
                  position: "absolute",
                  bottom: 2,
                  right: 2,
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: "#2563eb",
                  color: "white",
                  border: "2px solid white",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  padding: 0,
                }}
              >
                <i className="fa fa-camera" />
              </button>
            </div>
            <h3 className="font-medium text-white mt-2">{user.fullName}</h3>
            <p className="text-sm text-white/80">{user.agency.name}</p>
          </div>
        </div>
        <a
          href="/agency/profile"
          className="block text-center text-sm py-2 transition-colors"
          style={{ backgroundColor: "var(--mantine-primary-color-light)", color: "var(--mantine-primary-color-light-color)" }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--mantine-primary-color-light-hover)"}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "var(--mantine-primary-color-light)"}
        >
          <Trans i18nKey="dashboard.yourAgency" /> <i className="fa fa-arrow-circle-right ml-1" />
        </a>
      </div>

      {eventos.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="bg-blue-500 text-white px-4 py-2 font-medium text-sm" style={{ backgroundColor: "var(--secondary-color)" }}>
            <i className="fa fa-calendar mr-2" />
            <Trans i18nKey="dashboard.widgets.eventos" />
          </div>
          <div className="max-h-[215px] overflow-y-auto">
            <ul className="divide-y divide-gray-100">
              {eventos.map((evento) => (
                <li key={evento.id}>
                  <a
                    href={`/event/view?id=${evento.id}`}
                    className="block px-4 py-3 hover:bg-gray-50 text-sm"
                  >
                    <div className="font-medium text-gray-900">{evento.name}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {new Date(evento.date_start).toLocaleDateString("pt-PT", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {visits.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="bg-blue-500 text-white px-4 py-2 font-medium text-sm" style={{ backgroundColor: "var(--secondary-color)" }}>
            <i className="fa fa-handshake-o mr-2" />
            <Trans i18nKey="dashboard.widgets.visits" />
          </div>
          <div className="max-h-[215px] overflow-y-auto">
            <ul className="divide-y divide-gray-100">
              {visits.map((visit) => (
                <li key={visit.id}>
                  <a
                    href={`/agency-visits/${visit.id}`}
                    className="block px-4 py-3 hover:bg-gray-50 text-sm"
                  >
                    <div className="font-medium text-gray-900">
                      {new Date(visit.date).toLocaleDateString("pt-PT", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      })}
                    </div>
                    {visit.reason && (
                      <div className="text-xs text-gray-400 mt-0.5">{visit.reason}</div>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {integrations.length > 0 && (
        <div className="space-y-2">
          {integrations.map((integration) => (
            <IntegrationCard key={integration.id} integration={integration} />
          ))}
        </div>
      )}

      {extraCards.length > 0 && (
        <div className="space-y-2">
          {extraCards.map((card) => (
            <IntegrationCard key={card.id} integration={card} />
          ))}
        </div>
      )}
    </div>
  )
}

export default LeftSidebar